import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { closeUserProfileModal } from "actions/ui";
import ModalPortal from "components/ModalPortal";
import { withTranslation } from "react-i18next";
import { get } from "lodash";

import { UserTabList, UserTab, UserTabPanels, UserTabPanel } from "./UserTabs";
import PersonalData from "./PersonalData";
import NotificationChannels from "./NotificationChannels";
import NotificationSettings from "./NotificationSettings";
import permissions, { checkPermissions } from "config/permissions";
import "./styles.scss";

const mapStateToProps = state => ({
    closeUserProfileModal: state.ui.closeUserProfileModal,
    user: state.user,
    userProfileModalContext: state.ui.userProfileModalContext
});

const mapDispatchToProps = dispatch => ({
    closeUserProfileModal: () => dispatch(closeUserProfileModal()),
});

const UserProfileModal = props => {
    const {
        closeUserProfileModal,
        t,
        userProfileModalContext,
        user: { name, initials, partiesId },
    } = props;

    const isOrderNotifications = get(userProfileModalContext, 'isOrderNotifications');
    const orderId = get(userProfileModalContext, 'orderId');

    const canViewNotificationChannels = checkPermissions(
        permissions.AccountSettingsOperations.view_notificationChannels
    );
    const canEditNotificationChannels = checkPermissions(
        permissions.AccountSettingsOperations.edit_notificationChannels
    );
    const canViewnotificationSubscriptions = checkPermissions(
        permissions.AccountSettingsOperations.view_notificationSubscriptions
    );
    const canEditNotificationSubscriptions = checkPermissions(
        permissions.AccountSettingsOperations.edit_notificationSubscriptions
    );

    const [activeTab, setActiveTab] = useState(0);

    const onClose = useCallback(() => {
        closeUserProfileModal();
    }, [closeUserProfileModal]);

    const handleKeyPress = useCallback(
        e => {
            if (e.keyCode === 27) {
                onClose();
            }
        },
        [onClose]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKeyPress);
        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [handleKeyPress]);

    let tabs = [];
    if (isOrderNotifications && canViewnotificationSubscriptions) {
        tabs.push({
            label: t("userProfileModal.notificationSettings"),
            content: (
                <NotificationSettings
                    t={t}
                    canEditNotificationSubscriptions={canEditNotificationSubscriptions}
                    isOrderNotifications={isOrderNotifications}
                    orderId={orderId}
                />
            ),
        });
    } else {
        tabs.push({
            label: t("userProfileModal.personalData"),
            content: <PersonalData partiesId={partiesId} t={t} />,
        });

        if (canViewNotificationChannels) {
            tabs.push({
                label: t("userProfileModal.notificationChannels"),
                content: <NotificationChannels t={t} canEditNotificationChannels={canEditNotificationChannels} />,
            });
        }

        if (canViewnotificationSubscriptions) {
            tabs.push({
                label: t("userProfileModal.notificationSettings"),
                content: (
                    <NotificationSettings t={t} canEditNotificationSubscriptions={canEditNotificationSubscriptions} />
                ),
            });
        }
    }

    return (
        closeUserProfileModal && (
            <ModalPortal
                className="ordering-component-ui-core-wrapper user-profile-modal"
                onClose={onClose}
                svgClose={true}
            >
                <header className="modal-window-header">
                    <div className="modal-window-title">{t("userProfileModal.title")}</div>
                </header>
                <div className="modal-body">
                    <div className="user-info-mini">
                        <div className="user-initials">{initials}</div>
                        <div className="user-name">{name}</div>
                    </div>

                    <UserTabList activeTab={activeTab} setActiveTab={setActiveTab}>
                        {tabs.map(tab => (
                            <UserTab key={tab.label}>{tab.label}</UserTab>
                        ))}
                    </UserTabList>

                    <UserTabPanels activeTab={activeTab}>
                        {tabs.map(tab => (
                            <UserTabPanel key={tab.label}>{tab.content}</UserTabPanel>
                        ))}
                    </UserTabPanels>
                </div>
            </ModalPortal>
        )
    );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(UserProfileModal));
